import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isDateBefore } from '../helpers/dateParser';
import { GetCashAdvances } from '../services/CashAdvance/GetCashAdvance';
import { PostCashAdvances } from '../services/CashAdvance/PostCashAdvances';
import {
  LoanInterface,
  TableDataInterface,
  PaginationFintechInterface,
  FilterInterface,
  SearchCriteriaIdInterface,
  InstaCashTransactionDetailsInterface,
} from '../types/cashAdvanceInterface';

export const useCashAdvanceStore = defineStore('cashAdvance', () => {
  const activeRow = ref<null | number>(null);
  const loading = ref<boolean>(true);
  const loadingSearch = ref<boolean>(false);
  const loadingCards = ref<boolean>(false);
  const search = ref<string | null>(null);
  const searchOnEnter = ref<string | null>(null);
  const instaCashAccess = ref<boolean>(false);
  const hasReadyCash = ref<boolean>(false);
  const isReadyCashAdmin = ref<boolean>(false);
  const filtersChanged = ref<boolean>(false);
  const minToDate = ref<string>('');
  const showSelectedFieldsLength = ref<boolean>(false);
  const tempSelectedFields = ref<null | unknown>(null);
  const tempSelectedFieldsLength = ref<number>(0);
  const isButtonDisabled = ref<boolean>(true);
  const instaCashTransactionDetails = ref<InstaCashTransactionDetailsInterface>(
    {
      state: false,
      isLoading: false,
    }
  );
  const selectedFields = ref<FilterInterface>({
    datePicker: [{ fromDate: '', toDate: '' }],
    status: [],
    employeeStatus: [],
    purpose: [],
  });
  const selectedFieldsToFilter = ref<FilterInterface>({
    datePicker: [{ fromDate: '', toDate: '' }],
    status: [],
    employeeStatus: [],
    purpose: [],
  });
  const paginationFintech = ref<PaginationFintechInterface>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const selectedLoan = ref<{ data: LoanInterface }>({
    data: {
      id: 0,
      firstName: 'string',
      middleName: 'string',
      lastName: 'string',
      email: 'string',
      employmentStatus: 'string',
      referenceId: 'string',
      loanStatus: 'string',
      loanProviderName: 'string',
      interestAmount: 0,
      convenienceFee: 0,
      loanAmount: 0,
      loanTotalAmount: 0,
      totalAmountDue: 0,
      totalAmountPaid: 0,
      totalPenalty: 0,
      applicationDate: '2023-09-07T11:42:33.177Z',
      firstDueDate: '2023-09-07T11:42:33.177Z',
      lastDueDate: '2023-09-07T11:42:33.177Z',
      disbursementDate: '2023-09-07T11:42:33.177Z',
      reason: {
        category: 'string',
        additionalDetails: 'string',
      },
      payments: [
        {
          period: 0,
          amountDue: 0,
          dueDate: '2023-09-07T11:42:33.177Z',
          paymentStatus: 'string',
          penalty: 0,
        },
      ],
    },
  });
  const filtersDrawer = ref<boolean>(false);
  const tableData = ref<TableDataInterface>({
    total: {
      requested: 0,
      disbursed: 0,
      paid: 0,
      cancelled: 0,
      totalRequests: 0,
      totalAmountBorrowed: 0,
    },
    data: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 0,
      links: {
        next: 'string',
        previous: 'string',
        last: 'string',
      },
    },
  });

  const loadingShow = () => {
    loading.value = true;
  };
  const loadingHide = () => {
    loading.value = false;
  };

  const changePage = async (paginate: PaginationFintechInterface) => {
    paginationFintech.value.currentPage = paginate.currentPage;
    paginationFintech.value.itemsPerPage = paginate.itemsPerPage;
    await getTableData();
  };
  const getSearch = async () => {
    loadingSearch.value = true;
    loadingCards.value = true;
    paginationFintech.value.currentPage = 1;

    searchOnEnter.value = search.value;
    await getTableData();

    instaCashTransactionDetails.value.state = false;
    activeRow.value = null;
  };

  const getTableData = async () => {
    const getEmployeeStatus = computed(() => {
      if (selectedFieldsToFilter.value.employeeStatus.length === 1) {
        return selectedFieldsToFilter.value.employeeStatus[0];
      } else {
        return '';
      }
    });
    try {
      const response: SearchCriteriaIdInterface = await PostCashAdvances({
        pageNumber: paginationFintech.value.currentPage,
        totalPages: paginationFintech.value.totalPages,
        totalItems: paginationFintech.value.totalItems,
        itemsPerPage: paginationFintech.value.itemsPerPage,
        rowsPerPage: paginationFintech.value.itemsPerPage,
        searchField: search.value,
        purposeId: selectedFieldsToFilter.value.purpose,
        loanStatusId: selectedFieldsToFilter.value.status,
        employmentStatus: getEmployeeStatus.value,
        dateStart: selectedFieldsToFilter.value.datePicker[0].fromDate,
        dateEnd: selectedFieldsToFilter.value.datePicker[0].toDate,
      });
      const res: TableDataInterface = await GetCashAdvances(
        response.searchCriteriaId
      );

      tableData.value = res;
      paginationFintech.value.currentPage = res.pagination.currentPage;
      paginationFintech.value.totalPages = res.pagination.totalPages;
      paginationFintech.value.totalItems = res.pagination.totalItems;
      paginationFintech.value.itemsPerPage = res.pagination.itemsPerPage;
    } catch (error) {
      console.error(error);
    }
    loadingSearch.value = false;
    loadingCards.value = false;
  };

  const confirmFilter = async () => {
    loadingSearch.value = true;
    loadingCards.value = true;
    paginationFintech.value.currentPage = 1;
    selectedFieldsToFilter.value = selectedFields.value;
    await getTableData();
  };

  const resetSelectedFields = async () => {
    loadingSearch.value = true;
    loadingCards.value = true;
    paginationFintech.value.currentPage = 1;
    tempSelectedFields.value = null;
    tempSelectedFieldsLength.value = 0;
    await getTableData();
  };

  const selectedFieldsLength = computed(
    () =>
      Object.keys(selectedFields.value).filter((property) => {
        const value = selectedFields.value[property];
        if (Array.isArray(value)) {
          return value.some((item) =>
            Object.values(item).some((date) => date !== '')
          );
        }
        return value.length > 0;
      }).length
  );

  const isValidToDate = computed(() => {
    if (
      selectedFields.value?.datePicker[0].fromDate &&
      selectedFields.value?.datePicker[0].toDate
    ) {
      return isDateBefore(
        selectedFields.value.datePicker[0].toDate,
        selectedFields.value.datePicker[0].fromDate
      );
    }
    return false;
  });

  return {
    activeRow,
    filtersDrawer,
    selectedLoan,
    tableData,
    getTableData,
    paginationFintech,
    selectedFields,
    selectedFieldsLength,
    tempSelectedFields,
    search,
    searchOnEnter,
    getSearch,
    loadingSearch,
    loading,
    loadingShow,
    loadingHide,
    loadingCards,
    instaCashAccess,
    minToDate,
    showSelectedFieldsLength,
    isValidToDate,
    changePage,
    confirmFilter,
    isButtonDisabled,
    resetSelectedFields,
    filtersChanged,
    tempSelectedFieldsLength,
    instaCashTransactionDetails,
    hasReadyCash,
    isReadyCashAdmin,
  };
});
